/**
 * The dashboard's Active companies chart (SF-5124): customer companies active each month over
 * the series, stacked in three bands (Enterprise, Self-serve paid, Free), so the top line is the
 * total and each band is that group's share. Hovering a month shows each band's count and % of
 * the total. Drawn with Recharts like the other usage charts. Months an older API sends without
 * the `active` figures are left out; with none at all there is no chart (see activeSeries.ts).
 */
import { Typography, theme } from 'antd';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PlatformSeriesMonth } from '../../../components/usage/usageClient';
import { formatNumber, formatPercent } from '../../../utils/format';

import {
  ACTIVE_BANDS,
  activePoints,
  bandShare,
  type ActivePoint,
  type BandKey,
} from './activeSeries';

const { Text } = Typography;

interface ActiveTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: ActivePoint }>;
  label?: string;
}

/** The hover box: the month, its total, and each band's count and % of the total. */
export const ActiveTooltip: React.FC<ActiveTooltipProps> = ({ active, payload, label }) => {
  const { token } = theme.useToken();
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return (
    <div
      data-testid="active-companies-tooltip"
      style={{
        background: token.colorBgElevated,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadius,
        padding: '8px 12px',
      }}
    >
      <Text strong style={{ display: 'block' }}>
        {label}: {formatNumber(point.total)} active
      </Text>
      {[...ACTIVE_BANDS].reverse().map((b) => {
        const share = bandShare(point[b.key], point.total);
        return (
          <div key={b.key} style={{ whiteSpace: 'nowrap' }}>
            {b.label}: {formatNumber(point[b.key])}
            {share !== null && <Text type="secondary"> ({formatPercent(share, 0)})</Text>}
          </div>
        );
      })}
    </div>
  );
};

export const ActiveCompaniesChart: React.FC<{ months: PlatformSeriesMonth[]; height?: number }> = ({
  months,
  height = 280,
}) => {
  const { token } = theme.useToken();
  const colours: Record<BandKey, string> = {
    enterprise: token.colorPrimary,
    self_serve_paid: token.colorInfo,
    free: token.colorWarning,
  };
  const axisTick = { fontSize: 12, fill: token.colorTextSecondary };
  return (
    <div style={{ height }} data-testid="active-companies-chart">
      <ResponsiveContainer width="100%" height="100%">
        {/* The right margin leaves room for the last month label. */}
        <AreaChart data={activePoints(months)} margin={{ top: 8, right: 32, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
          <XAxis
            dataKey="label"
            tick={axisTick}
            tickLine={false}
            axisLine={{ stroke: token.colorBorderSecondary }}
            minTickGap={16}
          />
          <YAxis
            tick={axisTick}
            tickLine={false}
            axisLine={false}
            width={48}
            allowDecimals={false}
          />
          <Tooltip content={<ActiveTooltip />} />
          <Legend wrapperStyle={{ fontSize: 13 }} />
          {ACTIVE_BANDS.map((b) => (
            <Area
              key={b.key}
              type="monotone"
              dataKey={b.key}
              name={b.label}
              stackId="active"
              stroke={colours[b.key]}
              fill={colours[b.key]}
              fillOpacity={0.35}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
